import { VITE_ENV } from 'config/constants';
import { getCurrentUnitSystem, getLicensingStatus, getSystemFlags } from 'core/core.selectors';
import { getSelectedCompany } from 'entities/company/company.selectors';
import { getSelectedProperty } from 'entities/property/property.selectors';
import { getSelectedSeasonsIds } from 'entities/season/season.reducer';
import { useMemo, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { lazyRoute } from './lazy-route';

const loadSmartPlan = () => import('smart-plan');

interface SmartPlanProps {
  config: {
    unitSystem: string;
    org?: string;
    cropCycle: string[];
    token: string | null;
    environment?: string;
    baseRoute?: string;
    locale: string | null;
    appId?: string;
    workspaceId?: string;
    systemFlags?: unknown;
  };
}

const SmartPlan = lazyRoute(() => loadSmartPlan().then((module: { SmartPlan: FC<SmartPlanProps> }) => ({ default: module.SmartPlan })));

const LocalSmartPlan = ({ routeLevel = 'organization' }: { routeLevel?: 'organization' | 'property' }) => {
  const { i18n } = useTranslation();

  const company = useSelector(getSelectedCompany);
  const property = useSelector(getSelectedProperty);
  const selectedSeasonsIds = useSelector(getSelectedSeasonsIds);
  const currentUnitSystem = useSelector(getCurrentUnitSystem);
  const licenseStatus = useSelector(getLicensingStatus);
  const flags = useSelector(getSystemFlags);

  const config: SmartPlanProps['config'] = useMemo(
    () => ({
      unitSystem: currentUnitSystem,
      org: company?.id,
      token: localStorage.getItem('access_token'),
      baseRoute:
        routeLevel === 'organization'
          ? `/company/${company?.id}/smart-plan`
          : `/company/${company?.id}/property/${property?.id}/smart-plan`,
      environment: VITE_ENV,
      cropCycle: selectedSeasonsIds,
      locale: i18n.resolvedLanguage || i18n.language || localStorage.getItem('i18nextLng'),
      appId: licenseStatus?.apps[0].id,
      workspaceId: licenseStatus?.workspace.id,
      systemFlags: flags
    }),
    [company, currentUnitSystem, flags, i18n.language, i18n.resolvedLanguage, licenseStatus, property, routeLevel, selectedSeasonsIds]
  );

  return <SmartPlan key={`${property?.id || 'organization'}-${config.locale || 'default'}`} config={config} />;
};

export default LocalSmartPlan;
